// pages/entry-form/ReturnInfoPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Chip,
  Dialog, DialogContent, DialogActions, CircularProgress, Alert,
  Tooltip, alpha, Fade, Card, Stack, Autocomplete, MenuItem, Select,
  FormControl, InputLabel, FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, AssignmentReturn as ReturnIcon, Close as CloseIcon,
  CalendarToday as CalendarIcon, Inventory as ProductIcon,
  Person as PersonIcon, LocalOffer as PriceIcon, Warehouse as StockIcon,
  Discount as DiscountIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import {
  tradesService,
  ReturnInfo, ReturnInfoPayload,
  Product, SellerInfo,
  PurchasePriceList,
} from '../../services/tradesService';

const ACCENT       = '#FF9800';
const RETURN_TYPES = ['Retail', 'Wholesale'];

const usePermissions = () => {
  const { user } = useAuth();
  const p = user?.role?.permissions?.[0];
  return {
    canCreate: p?.create_permission ?? false,
    canUpdate: p?.update_permission ?? false,
    canDelete: p?.delete_permission || user?.role?.role_name === 'DATA_MANAGER' || user?.is_superuser,
  };
};

const fmt = (v: string | number) =>
  Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const today = () => new Date().toISOString().split('T')[0];

const formatDateForInput = (dateStr: string | Date): string => {
  if (!dateStr) return '';
  if (typeof dateStr === 'string' && dateStr.match(/^\d{2}-\d{2}-\d{4}/)) {
    const [datePart] = dateStr.split(' ');
    const [day, month, year] = datePart.split('-');
    return `${year}-${month}-${day}`;
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ── Form value types ──────────────────────────────────────────────────────────
interface FV {
  return_date:       string;
  seller:            SellerInfo | null;
  product:           Product | null;
  purchase_price:    PurchasePriceList | null;
  quantity_pcs:      string;
  sell_rate:         string;
  discount_amount:   string;   // ← NEW
  total_sales_price: string;
  cash_amount:       string;
  due_amount:        string;
  return_type:       string;
  remarks:           string;
}

// ── Form Dialog ───────────────────────────────────────────────────────────────
const ReturnFormDialog: React.FC<{
  open: boolean; editing: ReturnInfo | null; products: Product[]; sellers: SellerInfo[];
  onClose: () => void; onSaved: () => void;
}> = ({ open, editing, products, sellers, onClose, onSaved }) => {

  const [saving, setSaving]                     = useState(false);
  const [apiError, setApiError]                 = useState('');
  const [priceList, setPriceList]               = useState<PurchasePriceList[]>([]);
  const [priceListLoading, setPriceListLoading] = useState(false);
  const [sellerBatchQtys, setSellerBatchQtys]   = useState<Record<number, number>>({});

  const {
    control, handleSubmit, reset, watch, setValue,
    formState: { errors }, trigger,
  } = useForm<FV>({
    defaultValues: {
      return_date: today(), seller: null, product: null, purchase_price: null,
      quantity_pcs: '', sell_rate: '', discount_amount: '0',
      total_sales_price: '', cash_amount: '', due_amount: '',
      return_type: 'Retail', remarks: '',
    },
  });

  const qty                   = watch('quantity_pcs');
  const rate                  = watch('sell_rate');
  const discount              = watch('discount_amount');   // ← NEW
  const cash                  = watch('cash_amount');
  const total                 = watch('total_sales_price');
  const selectedProduct       = watch('product');
  const selectedSeller        = watch('seller');
  const selectedPurchasePrice = watch('purchase_price');

  const purchaseRate = selectedPurchasePrice
    ? parseFloat(selectedPurchasePrice.purchase_rate)
    : null;

  const originalQty = editing ? parseInt(String(editing.quantity_pcs || '0')) : 0;

  const sellerSoldQty = selectedPurchasePrice
    ? (sellerBatchQtys[selectedPurchasePrice.purchase_price_id] ?? 0)
    : null;

  const maxReturnQty = sellerSoldQty !== null
    ? sellerSoldQty + originalQty
    : null;

  // ── Fetch price list when product changes ─────────────────────────────────
  useEffect(() => {
    if (!selectedProduct) {
      setPriceList([]);
      setValue('purchase_price', null);
      setSellerBatchQtys({});
      return;
    }

    setPriceListLoading(true);
    tradesService
      .listPurchasePrices({ product_id: selectedProduct.product_id, with_stock: false })
      .then(res => {
        const filtered = res.results.filter(p => {
          const isCurrent = editing?.purchase_price === p.purchase_price_id;
          return p.sell_quantity > 0 || isCurrent;
        });
        setPriceList(filtered);

        if (editing && editing.purchase_price) {
          const found = res.results.find(p => p.purchase_price_id === editing.purchase_price);
          if (found) setValue('purchase_price', found);
        } else if (!editing && filtered.length === 1) {
          setValue('purchase_price', filtered[0]);
        } else {
          setValue('purchase_price', null);
        }
      })
      .catch(() => { setPriceList([]); setValue('purchase_price', null); })
      .finally(() => setPriceListLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.product_id]);

  // ── Fetch seller-specific net returnable qty per batch ────────────────────
  useEffect(() => {
    if (!selectedProduct || !selectedSeller) {
      setSellerBatchQtys({});
      return;
    }

    Promise.all([
      tradesService.listSales({
        product_id:     selectedProduct.product_id,
        seller_info_id: selectedSeller.seller_info_id,
      }),
      tradesService.listReturns({
        product_id:     selectedProduct.product_id,
        seller_info_id: selectedSeller.seller_info_id,
      }),
    ])
      .then(([salesRes, returnsRes]) => {
        const soldQtys: Record<number, number> = {};
        for (const sale of salesRes.results) {
          if (sale.purchase_price !== null) {
            soldQtys[sale.purchase_price] =
              (soldQtys[sale.purchase_price] ?? 0) + sale.quantity_pcs;
          }
        }

        const returnedQtys: Record<number, number> = {};
        for (const ret of returnsRes.results) {
          if (editing && ret.return_info_id === editing.return_info_id) continue;
          if (ret.purchase_price !== null) {
            returnedQtys[ret.purchase_price] =
              (returnedQtys[ret.purchase_price] ?? 0) + ret.quantity_pcs;
          }
        }

        const netQtys: Record<number, number> = {};
        for (const batchId of Object.keys(soldQtys)) {
          const id  = Number(batchId);
          const net = (soldQtys[id] ?? 0) - (returnedQtys[id] ?? 0);
          netQtys[id] = Math.max(0, net);
        }

        setSellerBatchQtys(netQtys);
      })
      .catch(() => setSellerBatchQtys({}));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.product_id, selectedSeller?.seller_info_id]);

  // ── Re-filter priceList when sellerBatchQtys loads ────────────────────────
  useEffect(() => {
    if (!selectedProduct || Object.keys(sellerBatchQtys).length === 0) return;
    setPriceList(prev =>
      prev.filter(p => {
        const isCurrent     = editing?.purchase_price === p.purchase_price_id;
        const sellerHasSold = (sellerBatchQtys[p.purchase_price_id] ?? 0) > 0;
        return sellerHasSold || isCurrent;
      })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerBatchQtys]);

  // ── Re-validate qty when purchase_price changes ───────────────────────────
  useEffect(() => {
    if (selectedPurchasePrice && qty) trigger('quantity_pcs');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPurchasePrice?.purchase_price_id]);

  // ── Auto-calculate total: (qty × rate) − discount ─────────────────────────
  useEffect(() => {
    const q = parseFloat(qty || '0');
    const r = parseFloat(rate || '0');
    const d = parseFloat(discount || '0');
    if (q > 0 && r > 0) {
      const gross = q * r;
      const net   = Math.max(0, gross - d);
      setValue('total_sales_price', net.toFixed(2));
    }
  }, [qty, rate, discount, setValue]);

  // ── Auto-calculate due ────────────────────────────────────────────────────
  useEffect(() => {
    const t = parseFloat(total || '0'), c = parseFloat(cash || '0');
    if (t >= 0 && c >= 0) setValue('due_amount', Math.max(0, t - c).toFixed(2));
  }, [total, cash, setValue]);

  // ── Reset form when dialog opens ──────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setApiError('');
    setPriceList([]);
    setSellerBatchQtys({});

    if (editing) {
      reset({
        return_date:       formatDateForInput(editing.return_date) || today(),
        seller:            sellers.find(s => s.seller_info_id === editing.seller_info) ?? null,
        product:           products.find(p => p.product_id === editing.product) ?? null,
        purchase_price:    null,
        quantity_pcs:      String(editing.quantity_pcs),
        sell_rate:         editing.sell_rate,
        discount_amount:   editing.discount_amount ?? '0',   // ← NEW
        total_sales_price: editing.total_sales_price,
        cash_amount:       editing.cash_amount,
        due_amount:        editing.due_amount,
        return_type:       editing.return_type,
        remarks:           editing.remarks || '',
      });
    } else {
      reset({
        return_date: today(), seller: null, product: null, purchase_price: null,
        quantity_pcs: '', sell_rate: '', discount_amount: '0',
        total_sales_price: '', cash_amount: '', due_amount: '',
        return_type: 'Retail', remarks: '',
      });
    }
  }, [open, editing, products, sellers, reset]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = async (data: FV) => {
    if (!data.product)        { setApiError('Please select a product.'); return; }
    if (!data.purchase_price) { setApiError('Please select a purchase price.'); return; }

    const enteredQty = parseInt(data.quantity_pcs || '0');
    const origQty    = editing ? parseInt(String(editing.quantity_pcs || '0')) : 0;
    const sellerQty  = sellerBatchQtys[data.purchase_price.purchase_price_id] ?? 0;
    const maxQty     = sellerQty + origQty;

    if (enteredQty > maxQty) {
      setApiError(
        `Return quantity (${enteredQty} pcs) exceeds what this seller purchased ` +
        `(${maxQty} pcs) from batch Tk. ${parseFloat(data.purchase_price.purchase_rate).toFixed(2)}.`
      );
      return;
    }

    setSaving(true); setApiError('');
    try {
      const discountVal = parseFloat(data.discount_amount || '0');
      const payload: ReturnInfoPayload = {
        return_date:       data.return_date,
        seller_info:       data.seller?.seller_info_id ?? null,
        product:           data.product.product_id,
        purchase_price:    data.purchase_price.purchase_price_id,
        quantity_pcs:      enteredQty,
        sell_rate:         parseFloat(data.sell_rate),
        discount_amount:   discountVal,                           // ← NEW
        total_sales_price: parseFloat(data.total_sales_price),
        cash_amount:       parseFloat(data.cash_amount || '0'),
        due_amount:        parseFloat(data.due_amount || '0'),
        return_type:       data.return_type,
        remarks:           data.remarks.trim(),
      };
      if (editing) await tradesService.updateReturn(editing.return_info_id, payload);
      else         await tradesService.createReturn(payload);
      onSaved(); onClose();
    } catch (e: any) {
      const errs = e?.data?.errors;
      setApiError(
        errs?.quantity_pcs?.[0]    ||
        errs?.sell_rate?.[0]       ||
        errs?.discount_amount?.[0] ||
        errs?.cash_amount?.[0]     ||
        e?.data?.message           ||
        'Something went wrong.'
      );
    } finally { setSaving(false); }
  };

  const displayQty     = maxReturnQty;
  const remainingColor =
    displayQty === null ? 'text.secondary' :
    displayQty <= 0     ? '#f44336' :
    displayQty <= 10    ? '#FF9800' : '#4CAF50';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}>

      {/* Header — fixed, never scrolls */}
      <Box sx={{
        background: `linear-gradient(135deg, ${ACCENT} 0%, #E65100 100%)`,
        px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReturnIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
            {editing ? 'Edit Return Entry' : 'New Return Entry'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5 }}><CloseIcon /></IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <DialogContent sx={{ px: 3, py: 3, overflowY: 'auto', flex: 1 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{apiError}</Alert>
          )}
          <Stack spacing={2.5}>

            {/* Row 1: Date + Return Type */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller name="return_date" control={control}
                rules={{
                  required: 'Date required',
                  max: { value: new Date().toISOString().split('T')[0], message: 'Future date not allowed' },
                }}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Return Date *" type="date"
                    error={!!errors.return_date} helperText={errors.return_date?.message}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: new Date().toISOString().split('T')[0] }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon fontSize="small" sx={{ color: ACCENT }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )} />

              <Controller name="return_type" control={control} rules={{ required: 'Required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.return_type}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <InputLabel>Return Type *</InputLabel>
                    <Select {...field} label="Return Type *">
                      {RETURN_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                    {errors.return_type && (
                      <FormHelperText>{errors.return_type.message}</FormHelperText>
                    )}
                  </FormControl>
                )} />
            </Stack>

            {/* Row 2: Product + Seller */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller name="product" control={control}
                rules={{ required: 'Product is required' }}
                render={({ field }) => (
                  <Autocomplete
                    options={products.filter(p => p.is_active)}
                    getOptionLabel={o => `${o.product_name} (${o.unit_of_measure})`}
                    value={field.value}
                    onChange={(_, v) => { field.onChange(v); setValue('purchase_price', null); }}
                    sx={{ flex: 1 }}
                    renderInput={params => (
                      <TextField {...params} label="Product *"
                        error={!!errors.product}
                        helperText={(errors.product as any)?.message}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <ProductIcon fontSize="small" sx={{ color: ACCENT }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                    )} />
                )} />

              <Controller name="seller" control={control}
                rules={{ required: 'Seller is required' }}
                render={({ field }) => (
                  <Autocomplete
                    options={sellers}
                    getOptionLabel={o => `${o.seller_name} (${o.mobile})`}
                    value={field.value}
                    onChange={(_, v) => field.onChange(v)}
                    sx={{ flex: 1 }}
                    renderInput={params => (
                      <TextField {...params} label="Seller *"
                        error={!!errors.seller}
                        helperText={(errors.seller as any)?.message}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <PersonIcon fontSize="small" sx={{ color: ACCENT }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                    )} />
                )} />
            </Stack>

            {/* Row 3: Purchase Price + Returnable Qty */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">

              <Controller name="purchase_price" control={control}
                rules={{ required: 'Purchase Price is required' }}
                render={({ field }) => (
                  <Autocomplete
                    options={priceList}
                    getOptionLabel={o => `Tk. ${parseFloat(o.purchase_rate).toFixed(2)}`}
                    value={field.value}
                    onChange={(_, v) => field.onChange(v)}
                    loading={priceListLoading}
                    disabled={!selectedProduct || priceListLoading}
                    isOptionEqualToValue={(opt, val) =>
                      opt.purchase_price_id === val.purchase_price_id
                    }
                    noOptionsText={
                      !selectedProduct ? 'Select a product first' : 'No sold batches available'
                    }
                    sx={{ flex: 1 }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.purchase_price_id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                          <Typography fontWeight={700} color={ACCENT}>
                            Tk. {parseFloat(option.purchase_rate).toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={params => (
                      <TextField {...params} label="Purchase Price *"
                        error={!!errors.purchase_price}
                        helperText={(errors.purchase_price as any)?.message || ''}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <PriceIcon fontSize="small" sx={{ color: ACCENT }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {priceListLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                    )} />
                )} />

              {/* Returnable Quantity — read-only */}
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label={editing ? 'Available to Return' : 'Returnable Quantity'}
                  value={displayQty !== null ? displayQty : '—'}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <StockIcon fontSize="small" sx={{ color: remainingColor }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    displayQty !== null && displayQty <= 0
                      ? 'Nothing left to return from this batch'
                      : displayQty !== null && displayQty <= 5
                        ? 'Very few returnable items'
                        : ' '
                  }
                  FormHelperTextProps={{ sx: { color: remainingColor } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: alpha(
                        displayQty === null ? '#9E9E9E' :
                        displayQty <= 0     ? '#f44336' :
                        displayQty <= 5     ? '#FF9800' : '#4CAF50',
                        0.05
                      ),
                    },
                    '& input': { fontWeight: 700, color: remainingColor },
                  }} />
              </Box>
            </Stack>

            {/* Row 4: Qty + Rate + Discount */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>

              <Controller name="quantity_pcs" control={control}
                rules={{
                  required: 'Required',
                  min: { value: 1, message: 'Must be > 0' },
                  validate: (value) => {
                    if (!selectedPurchasePrice) return true;
                    const entered    = parseInt(value || '0');
                    const origQty    = editing ? parseInt(String(editing.quantity_pcs || '0')) : 0;
                    const sellerQty  = sellerBatchQtys[selectedPurchasePrice.purchase_price_id] ?? 0;
                    const maxAllowed = sellerQty + origQty;
                    if (entered > maxAllowed) {
                      return `Exceeds returnable qty for this seller (${maxAllowed} pcs at Tk. ${parseFloat(selectedPurchasePrice.purchase_rate).toFixed(2)})`;
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Qty (Pcs) *" type="number"
                    error={!!errors.quantity_pcs}
                    helperText={
                      errors.quantity_pcs?.message ||
                      (maxReturnQty !== null
                        ? `Max returnable by this seller: ${maxReturnQty} pcs`
                        : '')
                    }
                    onWheel={e => (e.target as HTMLInputElement).blur()}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )} />

              <Controller name="sell_rate" control={control}
                rules={{ required: 'Required', min: { value: 0, message: '≥ 0' } }}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Sell Rate *" type="number"
                    inputProps={{ step: '0.01' }}
                    error={!!errors.sell_rate}
                    onWheel={e => (e.target as HTMLInputElement).blur()}
                    helperText={errors.sell_rate?.message || 'Original sell rate'}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">Tk. </InputAdornment>,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )} />

              {/* Discount Amount — NEW */}
              <Controller name="discount_amount" control={control}
                rules={{ min: { value: 0, message: 'Must be ≥ 0' } }}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Discount" type="number"
                    inputProps={{ step: '0.01' }}
                    error={!!errors.discount_amount}
                    onWheel={e => (e.target as HTMLInputElement).blur()}
                    helperText={errors.discount_amount?.message || 'Deducted from gross total'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DiscountIcon fontSize="small" sx={{ color: '#9C27B0' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        background: alpha('#9C27B0', 0.03),
                      },
                    }} />
                )} />
            </Stack>

            {/* Row 5: Total (auto) */}
            <Controller name="total_sales_price" control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Total Return Value" type="number"
                  inputProps={{ step: '0.01' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Tk. </InputAdornment>,
                  }}
                  onWheel={e => (e.target as HTMLInputElement).blur()}
                  helperText="Auto: (Sell Rate × Qty) − Discount"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: alpha(ACCENT, 0.03),
                    },
                  }} />
              )} />

            {/* Row 6: Cash + Due */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller name="cash_amount" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Cash Returned" type="number"
                    inputProps={{ step: '0.01' }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">Tk. </InputAdornment>,
                    }}
                    onWheel={e => (e.target as HTMLInputElement).blur()}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )} />

              <Controller name="due_amount" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Due Amount" type="number"
                    inputProps={{ step: '0.01' }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">Tk. </InputAdornment>,
                    }}
                    onWheel={e => (e.target as HTMLInputElement).blur()}
                    helperText="Auto from Total − Cash"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        background: alpha('#f44336', 0.03),
                      },
                    }} />
                )} />
            </Stack>

            {/* Remarks */}
            <Controller name="remarks" control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Remarks" multiline rows={2}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={onClose} disabled={saving} variant="outlined"
            sx={{ borderRadius: 2, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ borderRadius: 2, background: `linear-gradient(135deg, ${ACCENT} 0%, #E65100 100%)` }}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ── Delete Dialog ─────────────────────────────────────────────────────────────
const DeleteDialog: React.FC<{
  open: boolean; id: number | null; onClose: () => void; onDeleted: () => void;
}> = ({ open, id, onClose, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handle = async () => {
    if (!id) return;
    setLoading(true); setError('');
    try { await tradesService.deleteReturn(id); onDeleted(); onClose(); }
    catch (e: any) { setError(e?.data?.message || 'Failed.'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box sx={{
          width: 60, height: 60, borderRadius: '50%',
          background: alpha('#f44336', 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto', mb: 2,
        }}>
          <DeleteIcon sx={{ color: '#f44336', fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} mb={1}>Delete Return Entry</Typography>
        <Typography variant="body2" color="text.secondary">This action cannot be undone.</Typography>
        {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading}
          sx={{ borderRadius: 2, minWidth: 100 }}>Cancel</Button>
        <Button onClick={handle} variant="contained" color="error" disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          sx={{ borderRadius: 2, minWidth: 100 }}>
          {loading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const ReturnInfoPage: React.FC = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [rows, setRows]         = useState<ReturnInfo[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [sellers, setSellers]   = useState<SellerInfo[]>([]);
  const [totals, setTotals]     = useState<Record<string, number | null>>({});
  const [loading, setLoading]   = useState(true);

  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [returnType, setReturnType]     = useState('');
  const [filterProductId, setFilterProductId] = useState('');
  const [filterSellerId, setFilterSellerId]   = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing]   = useState<ReturnInfo | null>(null);
  const [delId, setDelId]       = useState<number | null>(null);

  useEffect(() => {
    tradesService.listProducts({ is_active: true }).then(r => setProducts(r.results)).catch(() => {});
    tradesService.listSellers().then(r => setSellers(r.results)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (dateFrom)        params.date_from      = dateFrom;
      if (dateTo)          params.date_to        = dateTo;
      if (returnType)      params.return_type    = returnType;
      if (filterProductId) params.product_id     = filterProductId;
      if (filterSellerId)  params.seller_info_id = filterSellerId;
      const res = await tradesService.listReturns(params);
      setRows(res.results); setTotals(res.totals || {});
    } catch {} finally { setLoading(false); }
  }, [dateFrom, dateTo, returnType, filterProductId, filterSellerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const RETURN_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
    Retail:    { bg: alpha('#2196F3', 0.1), color: '#2196F3' },
    Wholesale: { bg: alpha('#9C27B0', 0.1), color: '#9C27B0' },
  };

  const formatDate = (date: string | Date) => {
    let d: Date;
    if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}/)) {
      const [datePart, timePart] = date.split(' ');
      const [day, month, year]   = datePart.split('-');
      d = new Date(`${year}-${month}-${day}T${timePart || '00:00:00'}`);
    } else {
      d = new Date(date);
    }
    const day    = String(d.getDate()).padStart(2, '0');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${day}-${months[d.getMonth()]}-${d.getFullYear()}`;
  };

  return (
    <Fade in timeout={400}>
      <Box>
        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, flexWrap: 'wrap', gap: 1.5,
        }}>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{
              background: `linear-gradient(135deg, ${ACCENT} 0%, #E65100 100%)`,
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Return Information
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              Record and manage all return entries
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData} size="small"
                sx={{ border: `1px solid ${alpha(ACCENT, 0.3)}`, color: ACCENT }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} size="small"
                onClick={() => { setEditing(null); setFormOpen(true); }}
                sx={{
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${ACCENT} 0%, #E65100 100%)`,
                  boxShadow: `0 4px 12px ${alpha(ACCENT, 0.35)}`,
                }}>
                Add Return
              </Button>
            )}
          </Stack>
        </Box>

        {/* Summary Cards */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2.5 }}>
          {[
            { label: 'Total Records', value: rows.length,                           color: ACCENT },
            { label: 'Total Qty',     value: totals.total_qty ?? 0, suffix: ' Pcs', color: '#9C27B0' },
            { label: 'Total Value',   value: Number(totals.total_amount ?? 0), decimal: true, prefix: 'Tk. ', color: ACCENT },
            { label: 'Cash Returned', value: Number(totals.total_cash  ?? 0), decimal: true, prefix: 'Tk. ', color: '#4CAF50' },
            { label: 'Outstanding',   value: Number(totals.total_due   ?? 0), decimal: true, prefix: 'Tk. ', color: '#f44336' },
          ].map(c => (
            <Card key={c.label} elevation={0} sx={{
              flex: 1, p: 1.75, borderRadius: 3,
              border: `1px solid ${alpha(c.color, 0.2)}`,
              background: alpha(c.color, 0.04),
            }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                {c.label}
              </Typography>
              <Typography variant="h6" fontWeight={800}
                sx={{ color: c.color, lineHeight: 1.2, fontSize: '1rem' }}>
                {c.prefix}{c.decimal ? fmt(c.value as number) : c.value}{c.suffix}
              </Typography>
            </Card>
          ))}
        </Stack>

        {/* Filters */}
        <Card sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.1)}` }} elevation={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" flexWrap="wrap">

            <TextField size="small" label="From" type="date" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 148, '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />

            <TextField size="small" label="To" type="date" value={dateTo}
              onChange={e => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 148, '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />

            <FormControl size="small" sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}>
              <InputLabel>Product</InputLabel>
              <Select value={filterProductId} label="Product"
                onChange={e => setFilterProductId(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {products.map(p => (
                  <MenuItem key={p.product_id} value={String(p.product_id)}>{p.product_name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}>
              <InputLabel>Seller</InputLabel>
              <Select value={filterSellerId} label="Seller"
                onChange={e => setFilterSellerId(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {sellers.map(s => (
                  <MenuItem key={s.seller_info_id} value={String(s.seller_info_id)}>{s.seller_name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 148, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}>
              <InputLabel>Return Type</InputLabel>
              <Select value={returnType} label="Return Type"
                onChange={e => setReturnType(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {RETURN_TYPES.map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button size="small" variant="outlined"
              onClick={() => {
                setDateFrom(''); setDateTo(''); setReturnType('');
                setFilterProductId(''); setFilterSellerId('');
              }}
              sx={{
                borderRadius: 6, borderColor: alpha(ACCENT, 0.5), color: ACCENT,
                fontWeight: 600, px: 2,
                '&:hover': { borderColor: ACCENT, background: alpha(ACCENT, 0.06) },
              }}>
              Clear
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {rows.length} records
            </Typography>
          </Stack>
        </Card>

        {/* Table */}
        <Card elevation={0} sx={{
          borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.1)}`, overflow: 'hidden',
        }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: alpha(ACCENT, 0.04) }}>
                  {['#', 'Date', 'Product', 'Purchase Rate (Tk.)', 'Seller', 'Qty',
                    'Rate (Tk.)', 'Discount (Tk.)', 'Total (Tk.)', 'Cash (Tk.)', 'Due (Tk.)', 'Type', 'Created By', 'Actions'].map(h => (
                    <TableCell key={h} sx={{
                      fontWeight: 700, fontSize: '0.78rem', color: 'text.secondary', textAlign: 'center',
                      py: 1.5, borderBottom: `2px solid ${alpha(ACCENT, 0.12)}`, whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody> 
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={32} sx={{ color: ACCENT }} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 5 }}>
                      <ReturnIcon sx={{ fontSize: 40, color: 'text.disabled', display: 'block', mx: 'auto', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">No return entries found</Typography>
                    </TableCell>
                  </TableRow>
                ) : rows.map((r, idx) => {
                  const tc = RETURN_TYPE_COLORS[r.return_type] || { bg: alpha(ACCENT, 0.1), color: ACCENT };
                  return (
                    <TableRow key={r.return_info_id}
                      sx={{ '&:hover': { background: alpha(ACCENT, 0.03) } }}>
                      <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {formatDate(r.return_date)}
                      </TableCell>
                      <TableCell sx={{ py: 1.25, fontSize: '0.875rem', maxWidth: 130 }}>
                        <Typography noWrap fontWeight={600} fontSize="inherit">
                          {r.product_detail?.product_name ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.25, textAlign: 'right', fontSize: '0.82rem', color: '#9C27B0', fontWeight: 600 }}>
                        {r.purchase_price_detail ? `${fmt(r.purchase_price_detail.purchase_rate)}` : '—'}
                      </TableCell>
                      <TableCell sx={{ py: 1.25, fontSize: '0.8rem', maxWidth: 110 }}>
                        <Typography noWrap variant="body2" fontSize="inherit">
                          {r.seller_detail?.seller_name ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 700, color: '#9C27B0' }}>
                        {r.quantity_pcs}
                      </TableCell>
                      <TableCell sx={{ py: 1.25, textAlign: 'right', fontSize: '0.82rem' }}>
                        {fmt(r.sell_rate)}
                      </TableCell>
                      {/* Discount column — NEW */}
                      <TableCell sx={{ py: 1.25, textAlign: 'right', fontSize: '0.82rem', color: '#9C27B0' }}>
                        {parseFloat(r.discount_amount) > 0 ? fmt(r.discount_amount) : '—'}
                      </TableCell>
                      <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 700, color: ACCENT }}>
                        {fmt(r.total_sales_price)}
                      </TableCell>
                      <TableCell sx={{ py: 1.25, textAlign: 'right', color: '#4CAF50', fontWeight: 600 }}>
                        {fmt(r.cash_amount)}
                      </TableCell>
                      <TableCell sx={{ py: 1.25, textAlign: 'right', color: '#f44336', fontWeight: 600 }}>
                        {fmt(r.due_amount)}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <Chip size="small" label={r.return_type}
                          sx={{ background: tc.bg, color: tc.color, fontWeight: 700, height: 22, fontSize: '0.72rem' }} />
                      </TableCell>
                      <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.78rem' }}>
                        {r.created_by}
                      </TableCell>
                      <TableCell sx={{ py: 1.25 }}>
                        <Stack direction="row" spacing={0.5}>
                          {canUpdate && (
                            <Tooltip title="Edit">
                              <IconButton size="small"
                                onClick={() => { setEditing(r); setFormOpen(true); }}
                                sx={{ color: ACCENT, '&:hover': { background: alpha(ACCENT, 0.1) } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete">
                              <IconButton size="small"
                                onClick={() => setDelId(r.return_info_id)}
                                sx={{ color: '#f44336', '&:hover': { background: alpha('#f44336', 0.1) } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Totals row */}
                {rows.length > 0 && !loading && (
                  <TableRow sx={{ background: alpha(ACCENT, 0.06) }}>
                    <TableCell colSpan={5} sx={{ py: 1.25, fontWeight: 800, fontSize: '0.82rem', color: 'text.secondary' }}>
                      TOTALS
                    </TableCell>
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#9C27B0' }}>
                      {totals.total_qty ?? 0}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: ACCENT }}>
                      {fmt(totals.total_amount ?? 0)}
                    </TableCell>
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#4CAF50' }}>
                      {fmt(totals.total_cash ?? 0)}
                    </TableCell>
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 800, color: '#f44336' }}>
                      {fmt(totals.total_due ?? 0)}
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <ReturnFormDialog
          open={formOpen} editing={editing} products={products} sellers={sellers}
          onClose={() => setFormOpen(false)} onSaved={fetchData} />
        <DeleteDialog
          open={!!delId} id={delId}
          onClose={() => setDelId(null)} onDeleted={fetchData} />
      </Box>
    </Fade>
  );
};

export default ReturnInfoPage;